/**
 * Shared chrome for the AI Observability section — components only.
 *
 * Langfuse splits this surface into separate pages — Dashboards, Traces,
 * Sessions, Users, Scores — rather than stacking everything onto one scroll.
 * That is the right split for a reason worth stating: a dashboard answers
 * "what is happening across everything", and a list answers "what happened in
 * this one call". Mixing them means the aggregate view is mostly taken up by
 * twenty rows that answer neither question well.
 *
 * So the header, the tab strip and the time range live here, and each page
 * owns only its own content. The constants, formatters and fetch hook these
 * components use are in `obs-kit.ts`.
 */
import { Link, NavLink } from 'react-router-dom';

import { OBS_TIME_RANGES } from '../../types/ai-observability.types';
import { ICONS, OBS_TABS } from './obs-kit';

/** Plain stroked glyph — no gradient tile behind it. */
export function Icon({ d, size = 15 }: { d: string; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width={size} height={size}>
      <path d={d} />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round" width="15">
      <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
    </svg>
  );
}

export function ObsHeader({ subtitle, range, onRange, onRefresh, loading, meta }: {
  subtitle: string;
  range: string;
  onRange: (r: string) => void;
  onRefresh: () => void;
  loading?: boolean;
  /** Inline "2 traces · $0.00042" style summary, the way Langfuse states scope. */
  meta?: React.ReactNode;
}) {
  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 16, marginBottom: 14, flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 19, fontWeight: 600, letterSpacing: '-.015em' }}>
            AI Observability
          </h1>
          <div className="ea-sub" style={{ marginTop: 4 }}>
            {subtitle}
            {meta && <span style={{ marginLeft: 8 }}>· {meta}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          <div className="obs-seg">
            {OBS_TIME_RANGES.map((r) => (
              <button key={r.value} className={range === r.value ? 'on' : undefined}
                onClick={() => onRange(r.value)} disabled={loading}>
                {r.label}
              </button>
            ))}
          </div>
          <button aria-label="Refresh" className="obs-btn icon" title="Refresh" onClick={onRefresh} disabled={loading}>
            <RefreshIcon />
          </button>
          <Link to="/ai-observability/master" className="obs-btn" title="Observability Master — what tracing records"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none',
              padding: '5px 10px', fontSize: 12.5, color: 'inherit' }}>
            <Icon d={ICONS.gear} size={14} /> Configure
          </Link>
        </div>
      </div>

      <nav className="obs-tabs">
        {OBS_TABS.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end}
            className={({ isActive }) => (isActive ? 'on' : undefined)}>
            {t.label}
          </NavLink>
        ))}
      </nav>
    </>
  );
}

/** Card header: glyph, title, and the sentence saying what the chart shows. */
export function CardHead({ icon, title, sub, right }: {
  icon: string; title: string; sub?: string; right?: React.ReactNode;
}) {
  return (
    <div className="ea-card-head" style={{ alignItems: 'flex-start' }}>
      <div style={{ minWidth: 0 }}>
        <div className="ea-title">
          <span className="i"><Icon d={icon} /></span>
          {title}
        </div>
        {sub && <div className="ea-sub" style={{ marginTop: 3, marginLeft: 25 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

/**
 * A failed request, shown rather than swallowed.
 *
 * "No AI traffic yet" and "the rollup query is broken" produce the same empty
 * page, and they call for opposite reactions — so the error says which.
 * Renders nothing when there is no error, so callers need no guard.
 */
export function ErrorCard({ error, gap }: { error: string | null; gap?: boolean }) {
  if (!error) return null;
  return (
    <section className="ea-card pad"
      style={{ marginBottom: gap ? 16 : undefined, borderColor: '#FCA5A5' }}>
      <div className="ea-title" style={{ color: '#B91C1C' }}>Could not load</div>
      <div className="ea-sub" style={{ marginTop: 5 }}>{error}</div>
    </section>
  );
}

/** Breadcrumb back to the list a detail page was opened from. */
export function BackLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="ea-sub"
      style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <Icon d={ICONS.back} size={13} /> {children}
    </Link>
  );
}

/**
 * Loading, empty or nothing — the one-line states every list card shares.
 *
 * Silent when the request failed: the error card above already says so, and a
 * table reading "no executions in this window" under it would be a second,
 * wrong explanation for the same blank space.
 */
export function ObsState({ loading, error, rows, empty }: {
  loading: boolean;
  error: string | null;
  /** How many rows came back. */
  rows: number;
  /** What to say when the request succeeded and returned nothing. */
  empty: React.ReactNode;
}) {
  if (loading) return <div className="obs-empty">Loading…</div>;
  if (error || rows) return null;
  return <div className="obs-empty">{empty}</div>;
}
